import React, { Suspense, useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@material-ui/core";

import {
  HvButton,
  HvDialogActions,
  HvDialogContent,
  HvDialogTitle,
  HvFormStatus,
  HvGlobalActions,
  HvGrid,
  HvTypography,
} from "@hitachivantara/uikit-react-core";
import { MobileRotation } from "@hitachivantara/uikit-react-icons";

import {
  NotificationTemplate,
  useNotificationTemplate,
  WorkflowActionData,
  UsersDomain,
  UserDeviceAttribute,
  useUsers,
  Attribute,
} from "lib/api";

import Loading from "components/layout/Loading";
import UserSearchInput from "components/workflow/UserSearchInput";
import TagList, { HvTagListValue } from "components/common/TagList";
import TemplateSelector from "components/workflow/TemplateSelector";
import TemplatePreview from "components/workflow/TemplatePreview";

import { getUserDisplay } from "lib/utils/user";
import { DialogContentProps, userPredicate } from "../common";

import useStyles from "./styles";
import { getActionRecipientsList } from "../../Actions/utils";

const DialogContent: React.FC<DialogContentProps> = ({
  onCancel,
  onApply,
  actionType,
  initialValues,
}) => {
  const onCancelHandler = () => {
    onCancel?.();
  };

  const theme = useTheme();
  const classes = useStyles();
  const { t } = useTranslation("pushDialog");

  const { data: userData } = useUsers(
    {
      shouldRetryOnError: false,
    },
    {
      pageNr: -1,
      pageSize: -1,
      orderBy: encodeURIComponent("name,surname,email"),
      Order: "asc",
      includeGroups: "true",
      isExcludeServiceAccount: "true",
    }
  );

  const formatUser = useCallback(
    (user: Partial<UsersDomain>, inTag = false, notFound = false) => {
      const devices =
        !notFound && user.attributes != null
          ? Object.keys(user.attributes)
              .map<UserDeviceAttribute | undefined>((key) =>
                user.attributes != null &&
                key.startsWith("device_") &&
                "id" in user.attributes[key]
                  ? (user.attributes[key] as UserDeviceAttribute)
                  : undefined
              )
              .filter((a): a is UserDeviceAttribute => a != null)
          : [];
      const hasDevice = devices.length > 0;
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            paddingLeft: inTag && !hasDevice ? 10 : 20,
          }}
        >
          {hasDevice && (
            <MobileRotation
              style={{ position: "absolute", left: 0 }}
              iconSize="XS"
            />
          )}
          {`${getUserDisplay(user)} (${
            !hasDevice
              ? notFound
                ? t("recipients.userNotFound")
                : t("recipients.noDevice")
              : devices.map((attr) => attr.name).join(", ")
          })`}
        </span>
      );
    },
    [t]
  );

  const readExistingRecipients = useCallback((): Array<
    HvTagListValue<Partial<UsersDomain>>
  > => {
    const list = getActionRecipientsList(initialValues, actionType);

    return list.map((user): HvTagListValue<Partial<UsersDomain>> => {
      const contactUser = userData?.data.find((u) => u.id === user.id);
      if (!contactUser) {
        return {
          id: user.id as string,
          label: formatUser(user, true, true),
          color: theme.hv.palette.semantic.sema9,
          value: user,
        };
      }

      return {
        id: contactUser.id,
        label: formatUser(contactUser, true),
        color: theme.hv.palette.semantic.sema7,
        value: contactUser,
      };
    });
  }, [
    actionType,
    formatUser,
    initialValues,
    theme.hv.palette.semantic.sema9,
    theme.hv.palette.semantic.sema7,
    userData?.data,
  ]);

  const [recipients, setRecipients] = useState<
    Array<HvTagListValue<Partial<UsersDomain>>>
  >(() => readExistingRecipients());

  const { data: templateData } = useNotificationTemplate("push_templates", {
    revalidateOnFocus: false,
  });
  const [templates] = useState<NotificationTemplate[] | undefined>(
    templateData?.data.templates as NotificationTemplate[]
  );
  const [template, setTemplate] = useState<NotificationTemplate | undefined>(
    templates && templates[0]
  );

  const [recipientsUntouched, setRecipientsUntouched] = useState(true);
  const setRecipientsTouched = () => {
    if (recipientsUntouched) {
      setRecipientsUntouched(false);
    }
  };

  const [previewIsVisible, setPreviewIsVisible] = useState(false);
  useEffect(() => {
    if (initialValues && initialValues.notification_template_id && templates) {
      setTemplate(
        templates.filter(
          (temp) => temp.id === initialValues.notification_template_id
        )[0]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onRecipientsListChange = (
    newRecipients: Array<HvTagListValue<Partial<UsersDomain>>>
  ) => {
    setRecipientsTouched();
    setRecipients(newRecipients);
  };

  const onUserSelected = (user: UsersDomain) => {
    setRecipientsTouched();
    setRecipients([
      ...recipients,
      {
        id: user.id,
        label: formatUser(user, true),
        color: theme.hv.palette.semantic.sema7,
        value: user,
      },
    ]);
  };

  const validateUser = (user: UsersDomain) => {
    const duplicate =
      recipients.findIndex((recipient) => recipient.id === user.id) !== -1;
    if (duplicate) {
      return t("recipients.duplicateAddress");
    }

    return null;
  };

  const filterPredicate = (user: UsersDomain, searchString: string) =>
    userPredicate(user, searchString) && validateUser(user) == null;

  const tagListStatus: HvFormStatus = recipientsUntouched
    ? "standBy"
    : recipients.length > 0
    ? "valid"
    : "invalid";

  const handleTemplateChange = (
    _evt: unknown,
    newTemplate?: NotificationTemplate
  ) => {
    setTemplate(newTemplate);
    setRecipientsTouched();
  };

  const onApplyHandler = () => {
    if (recipients.length > 0 && template != null && actionType != null) {
      const attributes: Record<string, Attribute> = {};
      Object.keys(actionType.attributes).forEach((key) => {
        const attribute = actionType.attributes[key];
        attributes[key] = { ...attribute };

        if (attribute.type === "text") {
          if (attribute.label.toLocaleLowerCase().indexOf("body") !== -1) {
            attributes[key].value = template.body;
          }
        } else if (attribute.type === "array") {
          if (attribute.label.toLocaleLowerCase().indexOf("id") !== -1) {
            attributes[key].value = recipients.map(
              ({ value: { id, name, surname, email } }) => ({
                id,
                name,
                surname,
                email,
              })
            );
          }
        }
      });

      const data: WorkflowActionData = {
        continueOnError: false,
        continueOnNoResult: false,
        isInvalid: false,

        ...initialValues,

        fk_action_name: actionType.action_name,
        fk_notification_template_id: template.template_nr,
        attributes,
        // we'll need calculate this before sending to server
        exe_order: -1,
      };

      onApply?.({
        ...initialValues,
        ...data,
      });
    }
  };

  return (
    <>
      <HvDialogTitle className={classes.dialogTitle}>
        {t("title")}
      </HvDialogTitle>
      <HvDialogContent className={classes.dialogContent}>
        <Suspense fallback={<Loading delay={1000} loadingLabel="" />}>
          <HvGrid container spacing={0}>
            <HvGrid item xs={12} className={classes.recipientsSectionTitle}>
              <HvGlobalActions
                title={t("recipients.sectionTitle")}
                variant="section"
              />
              <HvTypography variant="normalText">
                {t("recipients.sectionDescription")}
              </HvTypography>
            </HvGrid>
            <HvGrid item xs={12}>
              <UserSearchInput
                label={t("recipients.inputLabel")}
                placeholder={t("recipients.inputPlaceholder")}
                noMatchLabel={t("recipients.noMatch")}
                filterPredicate={filterPredicate}
                formatUser={formatUser}
                onUserSelected={onUserSelected}
                onBlur={setRecipientsTouched}
              />
            </HvGrid>
            <HvGrid item xs={12}>
              <TagList
                required
                label={t("recipients.listLabel")}
                value={recipients}
                status={tagListStatus}
                statusMessage={t("recipients.required")}
                onChange={onRecipientsListChange}
              />
            </HvGrid>
            <HvGrid item xs={12} className={classes.templateSectionTitle}>
              <HvGlobalActions
                title={t("template.sectionTitle")}
                variant="section"
              />
              <HvTypography variant="normalText">
                {t("template.sectionDescription")}
              </HvTypography>
            </HvGrid>
            <HvGrid item xs={12}>
              <TemplateSelector
                templateType="NotificationPush"
                value={template}
                previewIsVisible={previewIsVisible}
                onChange={handleTemplateChange}
                templates={templates}
                togglePreview={setPreviewIsVisible}
                label={t("template.radioGroupLabel")}
                hidePreviewTooltip={t("template.hidePreviewTooltip")}
                showPreviewTooltip={t("template.showPreviewTooltip")}
                showPreviewAriaLabel={t("template.showPreviewAriaLabel")}
              />
            </HvGrid>
            <HvGrid item xs={12}>
              <TemplatePreview
                visible={previewIsVisible}
                template={template}
                title={t("template.previewTitle")}
              />
            </HvGrid>
          </HvGrid>
        </Suspense>
      </HvDialogContent>
      <HvDialogActions>
        <HvButton
          category="ghost"
          onClick={onApplyHandler}
          disabled={
            (recipientsUntouched && recipients.length === 0) || template == null
          }
        >
          {t("buttons.save")}
        </HvButton>
        <HvButton category="ghost" onClick={onCancelHandler}>
          {t("buttons.cancel")}
        </HvButton>
      </HvDialogActions>
    </>
  );
};

export default DialogContent;
